/**
 * InfoTooltip — hover/click tooltip for additional context.
 * Used throughout the pitch deck to show assumptions & sources.
 */
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, X } from "lucide-react";

export default function InfoTooltip({ children, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span ref={ref} className={`relative inline-flex items-center ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#3B82F6]/20 hover:bg-[#3B82F6]/40 transition-colors ml-1.5 flex-shrink-0"
        aria-label="More information"
      >
        <Info className="w-3 h-3 text-[#60A5FA]" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 max-w-[90vw]"
          >
            <div className="bg-[#1A2332] border border-white/15 rounded-xl p-3.5 shadow-xl text-sm text-gray-300 leading-relaxed">
              {children}
              <button
                onClick={() => setOpen(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors md:hidden"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Arrow */}
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45 bg-[#1A2332] border-b border-r border-white/15" />
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
